import type { PessoaSimulacaoRemuneracao } from "@/hooks/use-remuneracao";
import { MESES_COMPETENCIA, formatarCompetencia } from "@shared/equipe-fechamento";

/**
 * A nota única da pessoa, em destaque, com a conta explícita por trás dela —
 * "o que decide o dinheiro, antes do dinheiro em si" (§2 da Ficha do mês).
 *
 * Extraído de `equipe/remuneracao.tsx` (aba Simulação) para ser reaproveitado
 * pela Ficha do mês (`equipe/ficha.tsx`) sem duas renderizações do mesmo
 * cálculo — o princípio #2 do redesenho do Financeiro: "cada número tem um
 * lugar canônico". Quando a aba Simulação sair (PR3 do redesenho), este
 * arquivo continua sendo o único dono deste bloco.
 */

// Os nomes dos meses e o rótulo da competência moram em shared/ — o recibo
// (que o servidor também pode montar) precisa deles, e duas listas de meses é
// como "agosto/2026" e "Agosto 2026" divergem em telas diferentes.
export const MESES_LABEL = MESES_COMPETENCIA;
export const competenciaLabel = formatarCompetencia;

/** 2 casas — conferência, não leitura rápida: aqui é o número que o Hugo confere na mão. */
export function nota2(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : v.toFixed(2).replace(".", ",");
}

/** "5 notas somando 37,27 ÷ 5 = 7,45" — a conta por trás da nota única, explícita. */
export function ContaDaMedia({ pessoa }: { pessoa: PessoaSimulacaoRemuneracao }) {
  if (pessoa.nota === null) return null;
  const notas = pessoa.setores.filter((s): s is typeof s & { nota: number } => !s.foraDaMedia && s.nota !== null).map(s => s.nota);
  if (!notas.length) return null;
  const soma = notas.reduce((a, b) => a + b, 0);
  return (
    <p className="text-xs text-muted-foreground">
      {notas.length} nota{notas.length === 1 ? "" : "s"} somando {nota2(soma)} ÷ {notas.length} = {nota2(pessoa.nota)}
    </p>
  );
}

/** Nota, faixa e os dois percentuais em destaque — o que decide o dinheiro, antes do dinheiro em si. */
export function DestaqueNota({ pessoa }: { pessoa: PessoaSimulacaoRemuneracao }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-2xl font-bold tabular-nums">{nota2(pessoa.nota)}</span>
        <span className="text-sm text-muted-foreground">
          {pessoa.semNota ? "sem nota no mês" : pessoa.rotulo ? pessoa.rotulo : "faixa não cadastrada"}
        </span>
      </div>
      <p className="text-sm">
        <span className="font-medium">{pessoa.percentualFixo === null ? "—" : `${pessoa.percentualFixo}%`}</span> fixo ·{" "}
        <span className="font-medium">{pessoa.percentualVariavel === null ? "—" : `${pessoa.percentualVariavel}%`}</span> variável
      </p>
      <ContaDaMedia pessoa={pessoa} />
      {pessoa.semNota && (
        <p className="text-xs text-muted-foreground">Sem nota no mês — 100% do fixo, 0% do variável.</p>
      )}
    </div>
  );
}
